import QuinceApiError from '../../domains/quince-api-error'
import { ITrackingDetails, ITrackingDetailsResponse } from '../../domains/Tracking'
import { GOLD_BAR_SUBCLASS } from '../../hooks/cart/constants/goldBar.constants'
import {
  ImageUploadResponse,
  Order,
  ProductReturnCard,
  RETURN_MODES,
  makeReturnLineItems,
} from '../../hooks/useReturnController'
import RETURN_TYPES from '../../hooks/useReturnController/types/ReturnTypes'
import { getRefundParameters } from '../../hooks/useReturnController/useReturnController.helpers'
import AuthUtil from '../../utils/authUtil'
import getCurrentUser from '../../utils/getCurrentUser'
import {
  quinceReturnServiceHttpClient,
  quinceServicesHttpClient,
  quinceServicesPublicHttpClient,
} from '../../utils/HttpClient'

import { RefundSummary, ReturnRequest, ReturnResponse, ReturnSummaryResponse } from './ordersAndReturn.types'

const FILE_KEY = 'file'
const INVALID_REQUEST_ERROR = 'Invalid request'
const SHIPMENT_NOT_FOUND_ERROR = 'shipment not found'

class OrdersAndReturn {
  /*
   * This return line item id to store credits that will be granted.
   */
  async getOrderByOrderId(orderId: number): Promise<Order> {
    const userSession = await AuthUtil.getUserSession()
    const { data: order } = await quinceServicesHttpClient.get<Order>(`/v2/customer/orders/${orderId}`, {
      headers: { Authorization: `Bearer ${userSession?.idToken?.jwtToken}` },
    })
    return order
  }

  async getReturnSummary(orderId: number, productReturnCards: ProductReturnCard[]): Promise<RefundSummary> {
    const { data: returnSummaryResponse } = await quinceServicesHttpClient.post<
      ReturnSummaryRequestBody,
      ReturnSummaryResponse
    >(`/returns/refund-amount`, {
      orderId,
      listReturnLineItemRequests: productReturnCards.map(productReturnCard => ({
        lineItemId: productReturnCard.lineItem.id,
        quantity: productReturnCard.returnQuantity,
        exchangeProductId:
          productReturnCard.returnType === RETURN_TYPES.EXCHANGE
            ? productReturnCard.exchangeDetails?.exchangeData.exchangeProductId
            : undefined,
        exchangeVariantId:
          productReturnCard.returnType === RETURN_TYPES.EXCHANGE
            ? productReturnCard.exchangeDetails?.exchangeData.exchangeVariantId
            : undefined,
        ...getRefundParameters(productReturnCard),
      })),
    })

    const refundSummary = {
      subtotal: 0,
      taxRefund: 0,
      refundEstimate: 0,
      discount: 0,
      storeCreditRefund: returnSummaryResponse.storeCreditRefundAmount || 0,
      originalPaymentRefund: returnSummaryResponse.cardRefundAmount || 0,
    }
    returnSummaryResponse.lineItemRefunds?.forEach(refundItem => {
      refundSummary.subtotal += refundItem.refundDistribution?.ITEM_PRICE_REFUND?.refundAmount ?? 0
      refundSummary.taxRefund += refundItem.refundDistribution?.TAX_REFUND?.refundAmount ?? 0
      refundSummary.refundEstimate += refundItem.totalRefund.refundAmount || 0
      refundSummary.discount += refundItem.refundDistribution?.DISCOUNT_REFUND?.refundAmount ?? 0
    })

    return refundSummary
  }

  async createReturn(orderId: number, productReturnCardsToReturn: ProductReturnCard[]) {
    const user = getCurrentUser()

    const customerName = user.family_name ? `${user.name} ${user.family_name}` : user.name

    const { data: returnResponse } = await quinceReturnServiceHttpClient.post<ReturnRequest, ReturnResponse>(
      '/returns',
      {
        orderId,
        returnStatus: 'REQUEST_CREATED',
        customerName,
        returnLineItems: productReturnCardsToReturn
          .map(productReturnCard => makeReturnLineItems(productReturnCard))
          .flat(),
      },
    )

    return returnResponse
  }

  async uploadImage(lineItemId: string, file: File) {
    const data = new FormData()
    data.append(FILE_KEY, file)

    const { data: returnSummaryResponse } = await quinceServicesHttpClient.post<FormData, ImageUploadResponse>(
      '/returns/upload-image',
      data,
      {
        params: {
          lineItemId: lineItemId,
        },
      },
    )

    return returnSummaryResponse
  }

  checkIfUserEligibleToBuyGoldBar = async (): Promise<boolean> => {
    const user = getCurrentUser()
    const userEmail = user?.email ? user.email : ''
    const { data: response } = await quinceServicesHttpClient.get('/v2/customer/check-eligibility', {
      params: {
        subClassId: GOLD_BAR_SUBCLASS,
        email: userEmail,
      },
    })
    return response
  }

  fetchTrackingData = async (
    orderId: string,
    options:
      | {
          shipmentId: string
          trackingId?: string
        }
      | { trackingId: string; shipmentId?: string }
      | { trackingId: string; shipmentId: string },
  ): Promise<ITrackingDetails> => {
    const shipmentId = 'shipmentId' in options ? options.shipmentId : undefined
    const trackingId = 'trackingId' in options ? options.trackingId : undefined

    if (!trackingId && !shipmentId) throw Error(INVALID_REQUEST_ERROR)

    try {
      const { data: trackingData } = await quinceServicesPublicHttpClient.get<ITrackingDetailsResponse>(
        `/order/${orderId}/track`,
        {
          params: {
            shipmentId,
            trackingId,
          },
        },
      )

      // We will have only one shipment in this shipments array. Took confirmation from Tracking/Orders team
      const shipment = trackingData.shipments?.[0]

      if (!shipment) {
        throw Error(SHIPMENT_NOT_FOUND_ERROR)
      }
      return {
        orderNumber: trackingData.orderNumber,
        shipment: shipment,
      }
    } catch (e) {
      throw new QuinceApiError({
        // @i18n-ignore
        apiName: 'order track',
        // @i18n-ignore
        message: 'Failed while fetching tracking detail in  orders service fetchTrackingData() runtime',
        error: e as Error,
      })
    }
  }
}

export interface ReturnSummaryRequestBody {
  orderId: number
  listReturnLineItemRequests: ListReturnLineItemRequestsEntity[]
}
export interface ListReturnLineItemRequestsEntity {
  lineItemId: number
  returnType: Omit<RETURN_TYPES, RETURN_TYPES.REFUND_TO_STORE_CREDIT>
  quantity: number
  exchangeProductId?: number
  exchangeVariantId?: number
  refundMethod?: RETURN_MODES
}

const ordersAndReturnService = new OrdersAndReturn()

export default ordersAndReturnService
